/**
 * Dónde se ve cada nota: dentro del mensaje que dice el servidor, o suelta en el timeline (D67).
 *
 * La charla con el asistente que empezó en un mensaje del cliente sigue en ese mensaje hasta que se
 * le escribe al cliente. Esa regla la aplica el conversation-service al crear la nota y la guarda en
 * su `messageId`; el front no deduce nada: una nota con `messageId` va en esa burbuja, una sin él va
 * suelta.
 *
 * El timeline de la base trae cada nota anidada dentro de su mensaje. Una que llega por socket o como
 * respuesta de un pedido puede ser de un mensaje que no está en pantalla —una página que todavía no se
 * cargó, o un mensaje que ya no existe—: ésa se guarda como fila suelta, con su `messageId` en
 * `noteMessageId`, y se ve suelta hasta que el mensaje se carga. Nunca se esconde.
 */
import type { NoteView, TimelineRow } from "@/services/conversations.api";
import { filaDeNota } from "./note-choices";

/** La nota de una fila suelta del timeline, como la arma el render. */
export function notaDeFila(r: TimelineRow): NoteView {
  return {
    noteId: r.payload.noteId ?? r.id,
    messageId: r.payload.noteMessageId ?? null,
    authorUserId: r.payload.authorUserId ?? 0,
    authorName: r.payload.authorName ?? "",
    content: r.payload.content ?? "",
    mentions: r.payload.mentions ?? [],
    createdAt: r.occurredAt,
    choices: r.payload.choices ?? null,
    replyTo: r.payload.replyTo ?? null,
    task: r.payload.task ?? null,
  };
}

/**
 * Pone una nota nueva —del socket o de un pedido— en su lugar, sin duplicarla. Con `messageId` y el
 * mensaje en pantalla, dentro de su burbuja; si no, suelta al final, hasta que el mensaje se cargue.
 * Si ya estaba, se deja como está: el socket y la respuesta del pedido traen la misma nota.
 */
export function ponerNota(rows: TimelineRow[], note: NoteView): TimelineRow[] {
  const yaEsta = rows.some(
    (r) =>
      (r.kind === "note" && r.id === note.noteId) ||
      (r.kind === "message" &&
        (r.payload.notes ?? []).some((n) => n.noteId === note.noteId)),
  );
  if (yaEsta) return rows;
  const enPantalla =
    !!note.messageId &&
    rows.some(
      (r) => r.kind === "message" && r.payload.messageId === note.messageId,
    );
  if (!enPantalla) return [...rows, filaDeNota(note)];
  return rows.map((r) =>
    r.kind === "message" && r.payload.messageId === note.messageId
      ? {
          ...r,
          payload: { ...r.payload, notes: [...(r.payload.notes ?? []), note] },
        }
      : r,
  );
}

export interface UbicacionDeNotas {
  /**
   * Por mensaje: las notas que llegaron sueltas y cuyo mensaje ya está en pantalla. Se ven en su
   * burbuja, junto con las que trajo el timeline.
   */
  porMensaje: Map<string, NoteView[]>;
  /** Las filas sueltas que no se dibujan sueltas: su mensaje está en pantalla. */
  adentro: Set<string>;
  /** Los mensajes en pantalla: «Agregar nota» en una nota sólo se escribe en una burbuja que está. */
  mensajes: Set<string>;
}

/**
 * Qué fila suelta va dentro de un mensaje, ahora que el mensaje se cargó. Si el timeline ya trajo la
 * misma nota dentro del mensaje, la suelta sólo se oculta: no se ve dos veces.
 */
export function ubicarNotas(rows: TimelineRow[]): UbicacionDeNotas {
  const mensajes = new Set<string>();
  const anidadas = new Set<string>();
  for (const r of rows) {
    if (r.kind !== "message" || !r.payload.messageId) continue;
    mensajes.add(r.payload.messageId);
    for (const n of r.payload.notes ?? []) anidadas.add(n.noteId);
  }

  const porMensaje = new Map<string, NoteView[]>();
  const adentro = new Set<string>();
  for (const r of rows) {
    const destino = r.kind === "note" ? r.payload.noteMessageId : null;
    if (!destino || !mensajes.has(destino)) continue;
    adentro.add(r.id);
    if (anidadas.has(r.id)) continue;
    porMensaje.set(destino, [
      ...(porMensaje.get(destino) ?? []),
      notaDeFila(r),
    ]);
  }
  return { porMensaje, adentro, mensajes };
}

/**
 * «Agregar nota» en una nota: el campo se abre en la burbuja donde está, si está en pantalla. Si no
 * —una nota general, o una suelta cuyo mensaje no se cargó— se responde desde el compose.
 */
export function mensajeDondeResponder(
  note: NoteView,
  mensajes: Set<string>,
): string | null {
  return note.messageId && mensajes.has(note.messageId) ? note.messageId : null;
}
